import clsx from 'clsx';
import { useEffect, useRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { fmtMonthDay, type ProjectDTO } from '@wd/shared';
import { Icon, StatusIcon, type IconName } from './icons';
import './ui.css';

// ---------- Logo（黑白，随深浅主题切换） ----------

export function LogoMark({ size = 18, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <>
      <img src="/wattsonic-mark-on-dark.svg" alt="" width={size} height={size} className="only-dark" style={{ display: 'block', flex: 'none', ...style }} />
      <img src="/wattsonic-mark-on-light.svg" alt="" width={size} height={size} className="only-light" style={{ display: 'block', flex: 'none', ...style }} />
    </>
  );
}

// ---------- Kbd ----------

export function Kbd({ children, small }: { children: ReactNode; small?: boolean }) {
  return <span className={clsx('wd-kbd', small && 'sm')}>{children}</span>;
}

export function Kbds({ keys, small }: { keys: string[]; small?: boolean }) {
  return (
    <span className="wd-kbds">
      {keys.map((k) => (
        <Kbd key={k} small={small}>
          {k}
        </Kbd>
      ))}
    </span>
  );
}

export function Hint({ keys, children }: { keys: string[]; children: ReactNode }) {
  return (
    <span className="wd-hint">
      <Kbds keys={keys} />
      <span>{children}</span>
    </span>
  );
}

// ---------- 头像 ----------

/** 头像颜色：按人（姓名）固定一个色相，同一个人在哪里都是同一种颜色（类似 Outlook） */
const AVATAR_HUES = [4, 24, 42, 88, 140, 168, 192, 212, 232, 262, 290, 322];
export function avatarHue(seed: string) {
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return AVATAR_HUES[h % AVATAR_HUES.length]!;
}

export function Avatar({ initial, seed, size = 18 }: { initial: string; seed?: string; size?: 18 | 20 | 24 | 32 | 44 | 16 }) {
  const font = size >= 44 ? 15 : size >= 32 ? 13 : size >= 24 ? 11 : 10;
  const key = seed ?? initial;
  return (
    <span
      className={clsx('wd-avatar', key && key !== '?' && 'colored')}
      style={{ width: size, height: size, fontSize: font, ['--av-h' as string]: avatarHue(key) }}
      title={seed}
    >
      {initial}
    </span>
  );
}

export function AIAvatar({ size = 18 }: { size?: number }) {
  return (
    <span className="wd-avatar" style={{ width: size, height: size }} aria-label="日报助手">
      <span className="ai-dot" />
    </span>
  );
}

// ---------- 按钮 ----------

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'text' | 'icon';
  mobile?: boolean;
  icon?: IconName;
  full?: boolean;
};

export function Button({ variant = 'ghost', mobile, icon, full, className, children, ...rest }: BtnProps) {
  return (
    <button
      type="button"
      className={clsx('wd-btn', variant, mobile && 'mobile', full && 'full', icon && children && 'with-icon', className)}
      {...rest}
    >
      {icon && <Icon name={icon} size={mobile ? 20 : 16} color={variant === 'primary' ? 'currentColor' : undefined} />}
      {children}
    </button>
  );
}

export function IconButton({
  name,
  label,
  mobile,
  color,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { name: IconName; label: string; mobile?: boolean; color?: string }) {
  return (
    <button type="button" className={clsx('wd-btn icon', mobile && 'mobile')} aria-label={label} title={label} {...rest}>
      <Icon name={name} size={mobile ? 20 : 16} color={color} />
    </button>
  );
}

// ---------- 表单控件 ----------

export function Checkbox({ checked, large }: { checked: boolean; large?: boolean }) {
  return (
    <span className={clsx('wd-check', checked && 'on', large && 'lg')} aria-hidden="true">
      {checked && <Icon name="check" size={12} color="var(--on-accent)" stroke={2} />}
    </span>
  );
}

export function Radio({ checked, large }: { checked: boolean; large?: boolean }) {
  return <span className={clsx('wd-radio', checked && 'on', large && 'lg')} aria-hidden="true" />;
}

export function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} className={clsx('wd-toggle', on && 'on')} onClick={() => onChange(!on)}>
      <span />
    </button>
  );
}

export function Segmented<T extends string>({ items, value, onChange }: { items: { value: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <span className="wd-seg" role="tablist">
      {items.map((it) => (
        <button key={it.value} role="tab" aria-selected={value === it.value} className={clsx(value === it.value && 'on')} onClick={() => onChange(it.value)}>
          {it.label}
        </button>
      ))}
    </span>
  );
}

// ---------- 布局片段 ----------

export function GroupLabel({ children, right, style }: { children: ReactNode; right?: ReactNode; style?: React.CSSProperties }) {
  return (
    <div className="wd-label" style={style}>
      <span className="name">{children}</span>
      {right}
    </div>
  );
}

export function KV({ k, children, width }: { k: string; children: ReactNode; width?: number }) {
  return (
    <div className="wd-kv">
      <span className="k" style={width ? { width } : undefined}>
        {k}
      </span>
      <div className="v">{children}</div>
    </div>
  );
}

export function Banner({ icon, children, right }: { icon?: IconName; children: ReactNode; right?: ReactNode }) {
  return (
    <div className="wd-banner" role="status">
      {icon && <Icon name={icon} size={14} />}
      <span className="grow">{children}</span>
      {right}
    </div>
  );
}

export function EmptyState({ icon, title, desc, children }: { icon: IconName; title: string; desc?: ReactNode; children?: ReactNode }) {
  return (
    <div className="wd-empty">
      <Icon name={icon} size={26} stroke={1} />
      <div className="title">{title}</div>
      {desc && <div className="desc">{desc}</div>}
      {children && <div className="actions">{children}</div>}
    </div>
  );
}

export function Skeleton({ w = '100%', h = 12, style }: { w?: number | string; h?: number; style?: React.CSSProperties }) {
  return <div className="wd-skel" style={{ width: w, height: h, ...style }} />;
}

export function Quote({ meta, children }: { meta: ReactNode; children: ReactNode }) {
  return (
    <div className="wd-quote">
      <div className="meta">{meta}</div>
      <div className="text">{children}</div>
    </div>
  );
}

export function Message({ avatar, name, meta, children }: { avatar: ReactNode; name: string; meta?: ReactNode; children: ReactNode }) {
  return (
    <div className="wd-msg">
      {avatar}
      <div className="body">
        <div className="head">
          <span className="name">{name}</span>
          {meta && <span className="meta">{meta}</span>}
        </div>
        <div className="text">{children}</div>
      </div>
    </div>
  );
}

/** 把被追问的那句话加上虚线下划线 */
export function WithVague({ text, phrase }: { text: string; phrase: string | null }) {
  if (!phrase) return <>{text}</>;
  const i = text.indexOf(phrase);
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <span className="wd-vague">{phrase}</span>
      {text.slice(i + phrase.length)}
    </>
  );
}

// ---------- 读数格 ----------

export function TickBar({ value, target, maxTicks = 50 }: { value: number; target: number; maxTicks?: number }) {
  const total = Math.min(Math.max(1, Math.round(target)), maxTicks);
  const on = Math.round((Math.min(value, target) / target) * total);
  return (
    <div className="wd-ticks" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={i < on ? 'on' : undefined} />
      ))}
    </div>
  );
}

export function PhaseBar({ index, count }: { index: number; count: number }) {
  return (
    <div className="wd-phases" style={{ gridTemplateColumns: `repeat(${count}, 1fr)` }} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className={i < index ? 'past' : i === index ? 'now' : undefined} />
      ))}
    </div>
  );
}

function daysUntil(date: string, today: string) {
  return Math.round((Date.parse(date) - Date.parse(today)) / 86_400_000);
}

export function Readout({ p, today }: { p: ProjectDTO; today: string }) {
  const blocked = p.status === 'blocked';
  const blockedDays = p.health.find((h) => h.kind === 'blocked')?.label.match(/\d+/)?.[0];
  const value = blocked ? (blockedDays ?? '—') : p.metric?.value != null ? formatNum(p.metric.value) : p.dueDate ? String(Math.max(0, daysUntil(p.dueDate, today))) : '—';
  const unit = blocked ? '天' : p.metric?.value != null ? `${p.metric.target != null && p.metric.unit !== 'mV' ? `/${formatNum(p.metric.target)} ` : ''}${p.metric.unit ?? ''}` : p.dueDate ? '天' : '';
  const due = p.dueDate ? daysUntil(p.dueDate, today) : null;
  return (
    <div className="wd-readout">
      <div className="head">
        <StatusIcon status={p.status} />
        <span className="c1 ellipsis">{p.name}</span>
        <span className="t-meta" style={{ flex: 'none' }}>
          {p.code}
        </span>
      </div>
      <div className="num">
        <span className="v">{value}</span>
        {unit && <span className="u">{unit}</span>}
      </div>
      <div className="bar">
        {!blocked && p.metric?.value != null && p.metric.target != null && p.metric.unit !== 'mV' ? (
          <TickBar value={p.metric.value} target={p.metric.target} />
        ) : p.phaseCount ? (
          <PhaseBar index={blocked ? p.phaseCount - 1 : (p.phaseIndex ?? 0)} count={p.phaseCount} />
        ) : null}
      </div>
      <div className="foot">
        {blocked ? (
          <>
            <span className="c-red ellipsis">阻塞{p.blockedReason ? ` · ${p.blockedReason}` : ''}</span>
            {p.blockedSince && <span style={{ flex: 'none' }}>自 {fmtMonthDay(p.blockedSince)}</span>}
          </>
        ) : (
          <>
            <span className="ellipsis">
              {[p.phase, p.dueDate ? fmtMonthDay(p.dueDate) : null].filter(Boolean).join(' · ')}
            </span>
            {due != null && <span className="c3" style={{ flex: 'none' }}>{due >= 0 ? `${due} 天` : `超期 ${-due} 天`}</span>}
          </>
        )}
      </div>
    </div>
  );
}

function formatNum(n: number) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// ---------- 弹窗 / 底部面板 ----------

export function Modal({
  width = 560,
  title,
  subtitle,
  onClose,
  children,
  footNote,
  footer,
}: {
  width?: number;
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footNote?: ReactNode;
  footer?: ReactNode;
}) {
  useEscape(onClose);
  return createPortal(
    <div className="wd-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="wd-modal" role="dialog" aria-modal="true" style={{ width }}>
        <div className="head">
          <span className="c1">{title}</span>
          {subtitle && <span className="c4 ellipsis">{subtitle}</span>}
          <span className="flex1" />
          <IconButton name="close" label="关闭" onClick={onClose} />
        </div>
        <div className="body">{children}</div>
        {(footer || footNote) && (
          <div className="foot">
            <span className="note">{footNote}</span>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function BottomSheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useEscape(onClose);
  return createPortal(
    <div className="wd-sheet-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="wd-sheet" role="dialog" aria-modal="true">
        <div className="grip" />
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function SheetItem({
  icon,
  title,
  sub,
  onClick,
  danger,
}: {
  icon?: IconName;
  title: string;
  sub?: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button type="button" className="wd-sheet-item" onClick={onClick}>
      {icon && <Icon name={icon} size={20} color={danger ? 'var(--status-blocked)' : 'var(--text-3)'} />}
      <span className="col flex1">
        <span className="t" style={danger ? { color: 'var(--status-blocked)' } : undefined}>
          {title}
        </span>
        {sub && <span className="s">{sub}</span>}
      </span>
    </button>
  );
}

function useEscape(onClose: () => void) {
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && ref.current();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);
}
